import React, { useState } from 'react';
import { MarkingCriteriaList } from './style';
import styled from 'styled-components';
import {
  SearchContainer,
  SearchIcon,
  Input,
} from '../../components2/HelpSidebar/style';
import Rubricsnew from '../../static/img/Rubricsnew.svg';
import banks from '../../static/icons/banks.svg';
import darkCheckCircleIcon from '../../static/icons/dark-check-circle.svg';
import FilterSquare from '../../static/img/filter-square.svg';
import RoundedDropDown from '../../components2/RoundedDropDown/index.jsx';
import Toast from '../Toast';
import {
  Frame5086,
  Frame5086Text,
} from '../GiveFeedback/style.js';
import { toast } from 'react-toastify';
import { isMarkingCriteria, isSuccessCriteria, isShortAnswer, isCommentBank, getItemTypeDisplay } from './rules';
import GenericCard from '../Shared/GenericCard';
import { isTeacher } from '../Header2/rules.js';
import { useProfile } from '../state/hooks.js';
import { useClassSubjects } from '../../hooks/useSubjects';
import Loader from '../Loader';
import { getJeddaiRole } from '../../utils/userAccessHelper.js';


export default function CommonPopup({
  isOpen,
  onClose,
  allMarkingCriterias,
  updateMarkingCriteria,
  itemType = 'Marking Template',
  hideTypeFilter = false,
  selectedItem = null,
  filterSubject = null,
  addEditLabel = null,
  onAddEdit = null,
}) {
  const { data: profile, isLoadingdata: isLoadingProfile } = useProfile();
  const { subjects: teacherSubjects } = useClassSubjects();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const selectedItemId = selectedItem?.id;

  const getFilteredItems = (items, selectedId, type) => {
    if (isMarkingCriteria(type)) {
      return items.filter(item =>
        !item.id || item.type === 'RUBRICS' || item.id === selectedId
      );
    }
    if (isShortAnswer(type)) {
      return items;
    }
    if (isSuccessCriteria(type)) {
      return items.filter(item => {
        if (item.id === selectedId) return true;
        const hasMarks = item.criterias?.some(c => c.marks !== undefined && c.marks !== null);
        const hasObjectives = item.criterias?.some(c => c.objectives && Array.isArray(c.objectives) && c.objectives.length > 0);
        return hasObjectives || !hasMarks;
      });
    }
    return items;
  };

  const getSubjectDisplayLabel = (s) => {
    const code = typeof s === 'string' ? s : (s?.subjectCode || s?.code || '');
    const title = typeof s === 'string' ? s : (s?.subjectName || s?.title || s?.subjectCode || s?.code || '');
    const key = (code || title).toString().trim().toLowerCase();
    if (!key) return '';
    const t = teacherSubjects?.find(
      (x) => (x.code || '').toString().toLowerCase() === key || (x.title || x.subjectName || '').toString().trim().toLowerCase() === (title || '').toString().trim().toLowerCase()
    );
    return (t?.title || t?.subjectName || title || code).toString().trim();
  };

  const hasUnassignedItems = React.useMemo(() => {
    const items = getFilteredItems(allMarkingCriterias, selectedItemId, itemType);
    return (items || []).some((item) => {
      const subjects = item?.metadata?.subjects?.length ? item.metadata.subjects : (item?.subjects || []);
      return !subjects || subjects.length === 0;
    });
  }, [allMarkingCriterias, selectedItemId, itemType]);

  const subjectOptions = React.useMemo(() => {
    const items = getFilteredItems(allMarkingCriterias, selectedItemId, itemType);
    const seen = new Set();
    const fromItems = [];
    (items || []).forEach((item) => {
      const subjects = item?.metadata?.subjects?.length ? item.metadata.subjects : (item?.subjects || []);
      subjects.forEach((s) => {
        const label = getSubjectDisplayLabel(s);
        if (!label) return;
        const key = label.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          if (!fromItems.includes(label)) fromItems.push(label);
        }
      });
    });
    const sorted = fromItems.sort((a, b) => (a || '').localeCompare(b || ''));
    return ['All Subjects', ...sorted, ...(hasUnassignedItems ? ['Unassigned'] : [])];
  }, [allMarkingCriterias, selectedItemId, itemType, teacherSubjects, filterSubject, hasUnassignedItems]);

  const [subjectFilter, setSubjectFilter] = useState('All Subjects');

  React.useEffect(() => {
    if (isOpen && filterSubject) {
      // filterSubject may be a code — resolve to display label for matching
      const resolved = getSubjectDisplayLabel(filterSubject);
      setSubjectFilter(resolved || filterSubject);
    }
  }, [isOpen, filterSubject]);

  const [markingCriterias, setMarkingCriterias] = useState(
    getFilteredItems(allMarkingCriterias, selectedItemId, itemType)
  );

  const jeddaiRole = getJeddaiRole(profile);

  React.useEffect(() => {
    const selectedItemId = selectedItem?.id;
    const filteredItems = getFilteredItems(allMarkingCriterias, selectedItemId, itemType);
    setMarkingCriterias(filteredItems);
  }, [allMarkingCriterias, selectedItem, itemType]);

  React.useEffect(() => {
    if (!isOpen || subjectFilter === 'All Subjects' || subjectOptions.length <= 1) return;
    const countFor = (subjFilter, items) => {
      if (subjFilter === 'All Subjects') return items.length;
      if (subjFilter === 'Unassigned') {
        return items.filter((item) => {
          const subjects = item?.metadata?.subjects?.length ? item.metadata.subjects : (item?.subjects || []);
          return !subjects || subjects.length === 0;
        }).length;
      }
      const sel = (subjFilter || '').toString().trim();
      return items.filter((item) => {
        const itemLabels = (item?.metadata?.subjects?.length ? item.metadata.subjects : (item?.subjects || []))
          .map(getSubjectDisplayLabel)
          .filter(Boolean);
        return itemLabels.some((label) => (label || '').toString().trim() === sel);
      }).length;
    };
    if (countFor(subjectFilter, markingCriterias) > 0) return;
    const withData = subjectOptions.find((opt) => countFor(opt, markingCriterias) > 0);
    setSubjectFilter(withData || 'All Subjects');
  }, [isOpen, subjectFilter, subjectOptions, markingCriterias]);

  function markingCriteriaUrl(id) {
    if (!isMarkingCriteria(itemType)) {
      return `/commentBanks/${id}`;
    }
    return `/tools/rubrics/${id}`;
  }

  if (!isOpen) return null;

  if (isLoadingProfile) {
    return <Loader />;
  }

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleSelect = (item) => {
    updateMarkingCriteria(item);
    onClose();
  };

  const handleFilterChange = (type, value) => {
    setFilterType(value);
  };

  const handleSubjectFilterChange = (_, value) => {
    setSubjectFilter(value);
  };

  const getFilterOptions = () => {
    return ['All', 'Rubric'];
  };

  const filteredItems = markingCriterias.filter((item) => {
    const titleMatches = item.title
      ?.toLowerCase()
      ?.includes(searchTerm.toLowerCase()) ?? false;

    const typeMatches = !isMarkingCriteria(itemType) || filterType === 'All' ||
      (filterType === 'Rubric' && item.type === 'RUBRICS');

    const subjectMatches = subjectFilter === 'All Subjects' || (() => {
      if (subjectFilter === 'Unassigned') {
        const subjects = item?.metadata?.subjects?.length ? item.metadata.subjects : (item?.subjects || []);
        return !subjects || subjects.length === 0;
      }
      const itemLabels = (item?.metadata?.subjects || item?.subjects || [])
        .map(getSubjectDisplayLabel)
        .filter(Boolean);
      const selected = (subjectFilter || '').toString().trim();
      return itemLabels.some((label) => (label || '').toString().trim() === selected);
    })();

    return titleMatches && typeMatches && subjectMatches;
  });

  return (
    <PopupOverlay>
      <PopupContainer>
        <PopupHeader>
          <PopupTitle>
            Select  {getItemTypeDisplay(itemType)}
          </PopupTitle>
          <CloseButton onClick={onClose}>
            ✕
          </CloseButton>
        </PopupHeader>

        <SearchContainer>
          <Input
            placeholder={`Search for ${isMarkingCriteria(itemType) ? 'rubrics' :
                isSuccessCriteria(itemType) ? 'success criteria' :
                  isShortAnswer(itemType) ? 'short answers' :
                    'comment banks'
              }`}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <SearchIcon src="/img/find-replace.png" />
        </SearchContainer>

        <FilterSection>
          <Frame5086>
            <FilterIconImg src={FilterSquare} alt="Filter" />
            <Frame5086Text>Filters:</Frame5086Text>
          </Frame5086>

          {isMarkingCriteria(itemType) && !hideTypeFilter && (
            <RoundedDropDown
              search={false}
              type={''}
              selectedIndex={(type, value) => handleFilterChange(type, value)}
              menuItems={getFilterOptions()}
              defaultValue={filterType}
            />
          )}

          {subjectOptions.length > 0 && (
            <RoundedDropDown
              search={false}
              type={''}
              selectedIndex={handleSubjectFilterChange}
              menuItems={subjectOptions}
              value={subjectFilter}
              defaultValue={subjectFilter}
            />
          )}

          {addEditLabel && onAddEdit && (
            <AddEditButton onClick={onAddEdit}>
              {addEditLabel}
            </AddEditButton>
          )}
        </FilterSection>

        <PopupMarkingCriteriaList>
          {filteredItems.map((item, index) => {
            const getBadgeConfig = (type) => {
              if (isSuccessCriteria(itemType)) {
                return {
                  icon: darkCheckCircleIcon,
                  label: 'Success Criterias'
                };
              }
              if (isShortAnswer(itemType)) {
                return {
                  icon: darkCheckCircleIcon,
                  label: 'Short Answer'
                };
              }
              if (!isMarkingCriteria(itemType)) {
                return {
                  icon: banks,
                  label: 'Comment Bank'
                };
              }
              return {
                icon: Rubricsnew,
                label: 'Rubric'
              };
            };

            return (
              <GenericCard
                key={index}
                item={item}
                type={isSuccessCriteria(itemType) ? "successCriteria" :
                  isShortAnswer(itemType) ? "shortAnswer" :
                    isMarkingCriteria(itemType) ? "markingCriteria" : "commentBank"}
                isSelected={selectedItem && selectedItem.id === item.id}
                onSelect={() => handleSelect(item)}
                showCheckbox={false}
                showActions={false}
                badgeConfig={getBadgeConfig(item.type)}
                isSystem={item.isSystem}
              />
            );
          })}
        </PopupMarkingCriteriaList>
      </PopupContainer>
    </PopupOverlay>
  );
}

const PopupOverlay = styled.div`
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PopupContainer = styled.div`
  background-color: #fff;
  width: 60%;
  height: 90vh;
  max-height: 90vh;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
`;

const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PopupTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-family: 'IBM Plex Sans';
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  flex-wrap: wrap;
`;

const FilterIconImg = styled.img`
  width: 20px;
  height: 20px;
`;


const PopupMarkingCriteriaList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const AddEditButton = styled.button`
  font-family: 'IBM Plex Sans';
  font-size: 13px;
  font-weight: 500;
  color: #7200E0;
  background: none;
  border: 1px solid #7200E0;
  border-radius: 20px;
  cursor: pointer;
  padding: 6px 14px;
  white-space: nowrap;
  margin-left: auto;

  &:hover {
    background: #f3f4f6;
    color: #5a00b3;
    border-color: #5a00b3;
  }
`;
